import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { Link, Outlet, useParams, useRouteContext, type ErrorComponentProps } from "@tanstack/react-router";
import { Button, LogoWordmark } from "@atrix-analytics/ui";
import { ApiError, errorMessage } from "../api/http";
import { projectQuery, regionsQuery } from "../api/queries";
import { AppShell } from "../components/app-shell";
import { WorkspaceProvider } from "../lib/workspace";

export function ProjectLayout() {
  const { projectId } = useParams({ from: "/p/$projectId" });
  const { me } = useRouteContext({ from: "/p/$projectId" });
  const { data: project } = useSuspenseQuery(projectQuery(projectId));
  const regions = useQuery(regionsQuery);
  const org =
    me.orgs.find((o) => o.id === project.org_id) ?? {
      id: project.org_id,
      name: "Organization",
      slug: "",
      role: "member" as const,
      created_at: project.created_at,
    };
  return (
    <WorkspaceProvider me={me} org={org} project={project} region={regions.data?.find((r) => r.code === project.region)}>
      <AppShell>
        <Outlet />
      </AppShell>
    </WorkspaceProvider>
  );
}

export function ProjectError({ error, reset }: ErrorComponentProps) {
  const notFound = error instanceof ApiError && error.status === 404;
  return (
    <main className="grid min-h-screen place-items-center px-4">
      <div className="flex max-w-sm flex-col items-center gap-3 text-center">
        <LogoWordmark />
        <h1 className="mt-4 font-display text-xl font-semibold">
          {notFound ? "Project not found" : "This project didn't load"}
        </h1>
        <p className="text-sm text-fg-muted">
          {notFound
            ? "It may have been deleted, or it belongs to an organization you're not a member of."
            : errorMessage(error)}
        </p>
        <div className="mt-2 flex gap-2">
          {!notFound && (
            <Button variant="secondary" onClick={reset}>
              Try again
            </Button>
          )}
          <Link to="/" className="inline-flex h-8.5 items-center rounded-md bg-accent px-3.5 text-sm font-medium text-accent-fg hover:bg-accent-hover">
            Go to my projects
          </Link>
        </div>
      </div>
    </main>
  );
}
